import { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ColumnSize, GridBlock } from '@client/config/layout';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { gridConfig } from '@client/config/grid';
import { track } from '@client/helpers/tracking';

import { getMaxColumnHeight } from './helpers';

export const useHidePopover = () => {
    const [showPopover, setShowPopover] = useState(true);

    const closePopover = useCallback(() => {
        setShowPopover(false);
    }, [setShowPopover]);

    return { showPopover, closePopover };
};

export const useGridTrack = (columnCount: number) => {
    const readyToTrack = useRef(false);

    useEffect(() => {
        if (!readyToTrack.current) {
            readyToTrack.current = true;

            return;
        }

        track('campaign_overview_change', { campaignColumns: columnCount });
    }, [columnCount]);
};

export const useGridHelpers = (columnCount: ColumnSize, width: number, gridBlocks: GridBlock[]) => {
    const { gridCalculation, bannerRatio, filteredGrid } = gridConfig.open;

    const categoryTabId = useSelector(getCategoryTabId);

    const maxColumnHeight = useMemo(() => getMaxColumnHeight(gridBlocks), [gridBlocks]);

    const extraHeightPercent = useMemo(() => {
        const extraHeight = (filteredGrid?.extraHeight as any)?.[categoryTabId] || 0;

        return (extraHeight * 100) / width;
    }, [width, filteredGrid, categoryTabId]);

    const gridHeight = useMemo(() => {
        const labelHeight = (100 / width) * 60;

        const rowHeight =
            columnCount === 1
                ? gridCalculation[columnCount].column / bannerRatio.h['2'] + labelHeight
                : (gridCalculation as any)[columnCount].column * bannerRatio.v['1'];

        return (
            maxColumnHeight * rowHeight +
            (maxColumnHeight - 1) * (gridCalculation as any)[columnCount].gutter +
            maxColumnHeight * extraHeightPercent
        );
    }, [width, extraHeightPercent, maxColumnHeight, columnCount, gridCalculation, bannerRatio]);

    const getCampaignStyles = useCallback(
        (block: GridBlock) => {
            if (columnCount === 1) {
                const labelHeight = (100 / width) * 60;
                const blockHeight =
                    gridCalculation[columnCount].column / bannerRatio.h[block.w as 1 | 2] +
                    labelHeight;

                return {
                    left: 0,
                    marginTop:
                        (blockHeight + gridCalculation[columnCount].gutter) * block.y +
                        block.y * extraHeightPercent,
                    width: 100,
                    paddingTop: blockHeight,
                };
            }

            return {
                left:
                    block.x * gridCalculation[columnCount].column +
                    block.x * gridCalculation[columnCount].gutter,
                marginTop:
                    block.y *
                        (gridCalculation[columnCount].column * bannerRatio.v[1] +
                            gridCalculation[columnCount].gutter) +
                    block.y * extraHeightPercent,
                width: gridCalculation[columnCount].column * bannerRatio.h[block.w as 1 | 2],
                paddingTop: gridCalculation[columnCount].column * bannerRatio.v[block.h as 1 | 2],
            };
        },
        [columnCount, width, gridCalculation, bannerRatio, extraHeightPercent],
    );

    return { getCampaignStyles, gridHeight };
};
